import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
  MDBCheckbox,
  MDBCol,
  MDBFile,
  MDBIcon,
  MDBInput,
  MDBListGroup,
  MDBListGroupItem,
  MDBRadio,
  MDBRow,
  MDBSelect,
  MDBSpinner,
  MDBSwitch,
  MDBTextArea,
} from 'mdb-react-ui-kit';
import ReactCountryFlag from 'react-country-flag';
import { generateClient } from 'aws-amplify/api';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { captureError } from '../../utils/sentryHelpers';
import { getLocalizedText } from '../../utils/localizedText';
import { getLanguageText, setLocalized } from '../../utils/mediathek';
import {
  BOOTH_PRODUCT_TYPES,
  isValidVariantCode,
  isVariantProduct,
  normalizeBoothNumber,
  normalizeVariant,
  variantCodesOf,
} from '../../utils/boothMap';
import { boothImageUrl, getBoothAssetUrl, removeBoothAsset, uploadBoothAsset } from '../../utils/boothAssets';
import {
  EMPTY_MATCH_RULE_FORM,
  EMPTY_PRICE_RULE_FORM,
  STANDARD_POSITION_KEYS,
  areaFeatureKeys,
  cleanKeyList,
  hasRuleValue,
  matchRuleFromForm,
  matchRuleToForm,
  newSurchargeRow,
  normalizePositionKey,
  offerRulePreview,
  priceRuleFromForm,
  priceRuleToForm,
} from '../../utils/boothOfferAdmin';
import { transformMoney } from '../../utils/money';
import { ConfirmModal, ErrorList, FlashToast, LocalizedInput, useFlashToast } from '../mediathek/MediathekShared';

const Booth3DView = lazy(() => import('../../components/booth-booking/Booth3DView'));

const client = generateClient();

const FEATURE = 'booth-admin';
const COMPONENT = 'AdminEditBooth';

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

const GET_BOOTH_QUERY = /* GraphQL */ `
  query GetBoothProduct($id: ID!) {
    getProduct(id: $id) {
      id
      sku
      type
      price
      stock
      visibleInShop
      allowedBuyerGroups
      productCategoryProductsId
      name {
        language
        text
      }
      invoiceName {
        language
        text
      }
      includes {
        language
        text
      }
      image {
        main {
          fileName
          alt
        }
        additional {
          fileName
          alt
        }
      }
      boothNumber
      boothVariant
      boothVariantAliases
      boothMatchRule
      boothPriceMode
      boothPriceRule
      boothArea
      boothWidth
      boothDepth
      boothHeight
      boothDimensionFile {
        fileName
        s3Path
        type
      }
      booth3DModel {
        fileName
        s3Path
        type
      }
      eventProducts(limit: 1000) {
        items {
          id
          price
          stock
          isAvailable
          createdAt
          boothPriceRule
          timeRangePrices {
            timeRange {
              start
              end
            }
            price
          }
          event {
            id
            startDate
            name {
              language
              text
            }
          }
        }
      }
      components(limit: 1000) {
        items {
          id
          qty
          order
          componentProduct {
            id
            name {
              language
              text
            }
          }
        }
      }
    }
  }
`;

const LIST_CATEGORIES_QUERY = /* GraphQL */ `
  query BoothProductCategories($limit: Int, $nextToken: String) {
    listProductCategories(limit: $limit, nextToken: $nextToken) {
      items {
        id
        name {
          language
          text
        }
        sortKey
      }
      nextToken
    }
  }
`;

const LIST_FAIR_EVENTS_QUERY = /* GraphQL */ `
  query BoothFairEvents($limit: Int, $nextToken: String) {
    listEvents(filter: { type: { eq: "fair" } }, limit: $limit, nextToken: $nextToken) {
      items {
        id
        startDate
        name {
          language
          text
        }
      }
      nextToken
    }
  }
`;

const BOOTH_NUMBERS_BY_TYPE_QUERY = /* GraphQL */ `
  query BoothNumbersByType($type: String!, $limit: Int, $nextToken: String) {
    productsByType(type: $type, filter: { boothNumber: { attributeExists: true } }, limit: $limit, nextToken: $nextToken) {
      items {
        id
        sku
        boothNumber
        name {
          language
          text
        }
      }
      nextToken
    }
  }
`;

const UPLOAD_PRODUCT_IMAGE_MUTATION = /* GraphQL */ `
  mutation UploadBoothImage($imageBuffer: String, $imageType: String) {
    uploadCompanyImage(imageBuffer: $imageBuffer, imageType: $imageType)
  }
`;

const CREATE_PRODUCT_MUTATION = /* GraphQL */ `
  mutation CreateBoothProduct($input: CreateProductInput!) {
    createProduct(input: $input) {
      id
    }
  }
`;

const UPDATE_PRODUCT_MUTATION = /* GraphQL */ `
  mutation UpdateBoothProduct($input: UpdateProductInput!) {
    updateProduct(input: $input) {
      id
    }
  }
`;

const EVENT_PRODUCT_FIELDS = /* GraphQL */ `
  id
  price
  stock
  isAvailable
  createdAt
  boothPriceRule
  timeRangePrices {
    timeRange {
      start
      end
    }
    price
  }
  event {
    id
    startDate
    name {
      language
      text
    }
  }
`;

const CREATE_EVENT_PRODUCT_MUTATION = /* GraphQL */ `
  mutation CreateBoothEventProduct($input: CreateEventProductInput!) {
    createEventProduct(input: $input) {
      ${EVENT_PRODUCT_FIELDS}
    }
  }
`;

const UPDATE_EVENT_PRODUCT_MUTATION = /* GraphQL */ `
  mutation UpdateBoothEventProduct($input: UpdateEventProductInput!) {
    updateEventProduct(input: $input) {
      ${EVENT_PRODUCT_FIELDS}
    }
  }
`;

// Booth products of one event (offer-code check before linking an offer to it; matching preview of the rule editor)
const EVENT_BOOTH_PRODUCTS_QUERY = /* GraphQL */ `
  query BoothEventProductsOfEvent($filter: ModelEventProductFilterInput, $limit: Int, $nextToken: String) {
    listEventProducts(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        price
        createdAt
        boothPriceRule
        timeRangePrices {
          timeRange {
            start
            end
          }
          price
        }
        product {
          id
          type
          sku
          price
          boothNumber
          boothVariant
          boothVariantAliases
          boothMatchRule
          boothPriceMode
          boothPriceRule
          boothArea
          name {
            language
            text
          }
        }
      }
      nextToken
    }
  }
`;

// Live hall plan of an event (preview of the match rule / example prices): active version, else the legacy areas
const PREVIEW_EVENT_PLAN_QUERY = /* GraphQL */ `
  query BoothPreviewEventPlan($id: ID!) {
    getEvent(id: $id) {
      id
      boothPlan {
        activeVersionId
      }
    }
  }
`;

const PREVIEW_AREA_FIELDS = /* GraphQL */ `
  id
  boothNumber
  planVersionId
  variant
  size
  bookable
  positionType
  zone
  openSides
`;

const PREVIEW_AREAS_BY_VERSION_QUERY = /* GraphQL */ `
  query BoothPreviewAreasByVersion($planVersionId: ID!, $limit: Int, $nextToken: String) {
    boothPlanAreasByPlanVersion(planVersionId: $planVersionId, limit: $limit, nextToken: $nextToken) {
      items {
        ${PREVIEW_AREA_FIELDS}
      }
      nextToken
    }
  }
`;

const PREVIEW_AREAS_BY_EVENT_QUERY = /* GraphQL */ `
  query BoothPreviewAreasByEvent($eventId: ID!, $limit: Int, $nextToken: String) {
    boothPlanAreasByEvent(eventId: $eventId, limit: $limit, nextToken: $nextToken) {
      items {
        ${PREVIEW_AREA_FIELDS}
      }
      nextToken
    }
  }
`;

const DELETE_EVENT_PRODUCT_MUTATION = /* GraphQL */ `
  mutation DeleteBoothEventProduct($input: DeleteEventProductInput!) {
    deleteEventProduct(input: $input) {
      id
    }
  }
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const EMPTY_FORM = {
  boothNumber: '',
  // variant product: ONE product for all booths of the hall plan with this code (data-variant)
  boothVariant: '',
  // other plan codes meaning this variant, comma-separated in the form (Product.boothVariantAliases)
  boothVariantAliases: '',
  type: 'event_booth',
  name: [],
  invoiceName: [],
  sku: '',
  price: '',
  boothArea: '',
  boothWidth: '',
  boothDepth: '',
  boothHeight: '',
  visibleInShop: false,
  allowedBuyerGroups: ['business'],
  productCategoryProductsId: null,
  image: null,
  boothDimensionFile: null,
  booth3DModel: null,
};

/** '' / null -> null, "12,5" -> 12.5, garbage -> NaN. */
function parseDecimal(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim().replace(',', '.');
  if (text === '') return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : NaN;
}

function numberToInput(value) {
  return value === null || value === undefined ? '' : String(value);
}

/**
 * Booth number check (decision 14.10.2026: no fixed pattern, the numbers come from the SVG; same rule as the hall
 * plan tab): error for a leading '#' (reserved for unnamed shapes), hints for inner whitespace and more than 32 characters.
 */
function checkBoothNumberInput(value) {
  const text = String(value ?? '').trim();
  if (!text) return { error: null, warnings: [] };
  if (text.startsWith('#')) return { error: 'reserved-id', warnings: [] };
  const warnings = [];
  if (/\s/.test(text)) warnings.push('id-whitespace');
  if (text.length > 32) warnings.push('id-too-long');
  return { error: null, warnings };
}

/** Booth number / offer code as stored: trimmed, inner whitespace collapsed, '' -> null. */
function cleanCode(value) {
  const text = String(value ?? '').trim().replace(/\s+/g, ' ');
  return text || null;
}

/**
 * Aliases text ("9, 9-OLD") -> list of codes (trimmed, duplicates and the variant code itself removed);
 * null when empty (never stored as []).
 */
function parseAliasText(text, variant) {
  const own = normalizeVariant(variant);
  const seen = new Set(own ? [own] : []);
  const list = [];
  String(text ?? '').split(/[,;\n]/).forEach((part) => {
    const code = cleanCode(part);
    const key = normalizeVariant(code);
    if (!code || seen.has(key)) return;
    seen.add(key);
    list.push(code);
  });
  return list.length > 0 ? list : null;
}

/** SKU suggestion of a booth product: 'CP-ST-' + normalized booth number or variant code ('' without one). */
function suggestedSku(boothNumber, boothVariant) {
  const key = normalizeBoothNumber(boothNumber) || normalizeVariant(boothVariant);
  return key ? `CP-ST-${key.replace(/\s+/g, '-')}` : '';
}

/** MultiLangString list from the API -> form state (null entries dropped, text never null). */
function toLocalizedState(list) {
  return (Array.isArray(list) ? list : [])
    .filter(Boolean)
    .map((entry) => ({ language: entry.language, text: entry.text || '' }));
}

/** Form state -> MultiLangString input (trimmed, empty texts dropped). */
function toLocalizedInput(list) {
  return (Array.isArray(list) ? list : [])
    .filter((entry) => entry && entry.language && (entry.text || '').trim())
    .map((entry) => ({ language: entry.language, text: entry.text.trim() }));
}

/** Stored File without __typename / extra keys (null when there is no s3Path). */
// Product.image is a MultiImage; UpdateProductInput.image replaces the whole attribute, so the
// editor keeps `additional` (gallery images, e.g. copied from a template) and sends it back.
function cleanImageEntry(entry) {
  if (!entry || !entry.fileName) return null;
  return { fileName: entry.fileName, alt: entry.alt || null };
}

function cleanMultiImage(image) {
  const main = cleanImageEntry(image?.main);
  const additional = (Array.isArray(image?.additional) ? image.additional : []).map(cleanImageEntry).filter(Boolean);
  if (!main && additional.length === 0) return null;
  return { main, additional };
}

function cleanFile(file) {
  if (!file || !file.s3Path) return null;
  return { fileName: file.fileName || null, s3Path: file.s3Path, type: file.type || null };
}

function newAssetPrefix() {
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return `booth-assets/new-${id}`;
}

/** includes (one MultiLangString per bullet and language) -> rows [{de, en}]. */
function includesToRows(includes) {
  const list = Array.isArray(includes) ? includes.filter(Boolean) : [];
  const de = list.filter((entry) => entry.language === 'de');
  const en = list.filter((entry) => entry.language === 'en');
  const count = Math.max(de.length, en.length);
  return Array.from({ length: count }, (_, i) => ({ de: de[i]?.text || '', en: en[i]?.text || '' }));
}

/** rows [{de, en}] -> includes list (empty texts dropped). */
function rowsToIncludes(rows) {
  const includes = [];
  rows.forEach((row) => {
    if (row.de.trim()) includes.push({ language: 'de', text: row.de.trim() });
    if (row.en.trim()) includes.push({ language: 'en', text: row.en.trim() });
  });
  return includes;
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:.+;base64,/, ''));
    reader.onerror = () => reject(reader.error || new Error('Could not read the file'));
    reader.readAsDataURL(file);
  });
}

/** Result of uploadCompanyImage: "{fileName=...}" or "{error=...}". */
function parseImageUploadResult(raw) {
  const text = String(raw || '');
  const errorMatch = /error=([^}]*)/.exec(text);
  if (errorMatch) throw new Error(errorMatch[1].trim() || 'Image upload failed');
  const fileName = text.replace(/{|}|fileName=/g, '').trim();
  if (!fileName) throw new Error('Image upload failed');
  return fileName;
}

async function fetchAllPages(query, rootField, variables = {}) {
  const items = [];
  let nextToken = null;
  do {
    const response = await client.graphql({ query, variables: { ...variables, nextToken } });
    const page = response.data[rootField];
    items.push(...(page?.items || []).filter(Boolean));
    nextToken = page?.nextToken || null;
  } while (nextToken);
  return items;
}

/**
 * client.graphql that keeps partial data. Amplify v6 rejects on any GraphQL error, e.g. when an
 * EventProduct references a deleted event (EventProduct.event is non-null, so AppSync nulls that
 * item and reports an error). The orphan items arrive as null and are filtered by the callers.
 */
async function graphqlAllowPartial(options, rootField, operation) {
  try {
    return await client.graphql(options);
  } catch (error) {
    if (!error?.data?.[rootField]) throw error;
    captureError(error, {
      feature: FEATURE,
      operation,
      consoleMessage: 'Booth query returned partial data',
      context: { variables: options.variables },
      tags: { component: COMPONENT, partial: 'true' },
    });
    return error;
  }
}

/**
 * Other variant products of an event sharing one of the codes (boothVariant or alias): one code per event,
 * as the hall plan tab refuses it (findVariantConflicts). Pages keep partial data (orphan EventProducts).
 */
/** All EventProducts (with product) of an event; pages keep partial data (orphan EventProducts arrive as null). */
async function loadEventProductsOfEvent(eventId) {
  const items = [];
  let nextToken = null;
  do {
    // eslint-disable-next-line no-await-in-loop
    const response = await graphqlAllowPartial({
      query: EVENT_BOOTH_PRODUCTS_QUERY,
      variables: { filter: { eventEventProductsId: { eq: eventId } }, limit: 1000, nextToken },
    }, 'listEventProducts', 'fetch-event-booth-products');
    const page = response.data.listEventProducts;
    items.push(...(page?.items || []).filter((item) => item && item.product));
    nextToken = page?.nextToken || null;
  } while (nextToken);
  return items;
}

async function findEventVariantConflicts(eventId, productId, codes) {
  if (!eventId || codes.length === 0) return [];
  const keys = new Set(codes);
  const conflicts = [];
  (await loadEventProductsOfEvent(eventId)).forEach((item) => {
    const product = item.product;
    if (!product || product.id === productId || !isVariantProduct(product)) return;
    if (conflicts.some((p) => p.id === product.id)) return;
    if (variantCodesOf(product).some((code) => keys.has(code))) conflicts.push(product);
  });
  return conflicts;
}

/**
 * Live areas + booth EventProducts of an event for the preview of the offer rules: the areas of the active hall plan
 * version (Event.boothPlan.activeVersionId), else the legacy areas without version.
 */
async function loadPreviewData(eventId) {
  const eventResponse = await client.graphql({ query: PREVIEW_EVENT_PLAN_QUERY, variables: { id: eventId } });
  const versionId = eventResponse.data.getEvent?.boothPlan?.activeVersionId || null;
  const [areas, eventProducts] = await Promise.all([
    versionId
      ? fetchAllPages(PREVIEW_AREAS_BY_VERSION_QUERY, 'boothPlanAreasByPlanVersion', { planVersionId: versionId, limit: 1000 })
      : fetchAllPages(PREVIEW_AREAS_BY_EVENT_QUERY, 'boothPlanAreasByEvent', { eventId, limit: 1000 })
        .then((list) => list.filter((area) => !area.planVersionId)),
    loadEventProductsOfEvent(eventId),
  ]);
  return {
    versionId,
    areas,
    eventProducts: eventProducts.filter((item) => BOOTH_PRODUCT_TYPES.includes(item.product.type)),
  };
}

/** Translation keys of the standard position types (free keys are shown as the plan delivers them). */
const POSITION_LABELS = {
  row: 'Row booth', corner: 'Corner booth', head: 'Head booth', island: 'Island booth',
};

/** Label of a position type like the booths table of the hall plan tab ("Corner booth"; free keys as delivered). */
const positionLabel = (value, t) => {
  const key = normalizePositionKey(value);
  if (!key) return '';
  return POSITION_LABELS[key] ? t(POSITION_LABELS[key]) : key;
};

const SURCHARGE_TYPE_LABELS = {
  percent: 'Percent',
  absolute: 'Amount (EUR)',
  rate: 'Own rate per m²',
};

const ROUND_SQM_LABELS = {
  none: 'No rounding',
  ceil: 'Round up',
  floor: 'Round down',
  round: 'Round',
};

/**
 * Chips of a key list (position types / zones of a match rule): suggested keys toggle on / off, further keys can be
 * typed (free text, Enter or "Add"); selected keys that are not suggested are shown as removable chips.
 */
function KeyChips({
  idPrefix, label, value, suggestions, labelOf, onChange, disabled, normalize = (v) => v,
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const selectedKeys = new Set((value || []).map((key) => String(key).toLowerCase()));
  const shown = cleanKeyList([...(suggestions || []), ...(value || [])]);
  const toggle = (key) => {
    const lower = String(key).toLowerCase();
    onChange(selectedKeys.has(lower)
      ? (value || []).filter((entry) => String(entry).toLowerCase() !== lower)
      : cleanKeyList([...(value || []), key]));
  };
  const add = () => {
    const key = normalize(String(draft).trim().replace(/\s+/g, ' '));
    if (!key) return;
    onChange(cleanKeyList([...(value || []), key]));
    setDraft('');
  };
  return (
    <div className="mb-3">
      <div className="small fw-bold mb-1">{label}</div>
      <div className="d-flex flex-wrap gap-1 mb-1">
        {shown.map((key) => {
          const active = selectedKeys.has(String(key).toLowerCase());
          return (
            <MDBBtn
              key={key}
              type="button"
              size="sm"
              color={active ? 'primary' : 'light'}
              className="text-none"
              aria-pressed={active}
              disabled={disabled}
              onClick={() => toggle(key)}
            >
              {active && <MDBIcon fas icon="check" className="me-1" />}
              {labelOf ? labelOf(key) : key}
            </MDBBtn>
          );
        })}
      </div>
      <div className="d-flex gap-2 align-items-center" style={{ maxWidth: '320px' }}>
        <input
          id={`${idPrefix}-add`}
          type="text"
          className="form-control form-control-sm"
          value={draft}
          placeholder={t('Other key (free text)')}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <MDBBtn type="button" size="sm" outline disabled={disabled || !draft.trim()} onClick={add}>
          {t('Add')}
        </MDBBtn>
      </div>
      <div className="form-text">{t('No key selected = no condition.')}</div>
    </div>
  );
}

function sortEventProducts(items) {
  return [...items].sort((a, b) => new Date(b.event?.startDate || 0) - new Date(a.event?.startDate || 0));
}

function FlagLabel({ language }) {
  return (
    <ReactCountryFlag
      countryCode={language === 'en' ? 'gb' : language}
      svg
      style={{ width: '20px', height: '20px', marginTop: '10px' }}
    />
  );
}

function PreviewSpinner() {
  return (
    <div className="d-flex justify-content-center align-items-center" style={{ height: '320px' }}>
      <MDBSpinner role="status" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdminEditBooth() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { productId } = useParams();
  const isEdit = Boolean(productId);

  const [form, setForm] = useState(EMPTY_FORM);
  const formRef = useRef(form);
  formRef.current = form;
  const [includesRows, setIncludesRows] = useState([]);
  const [eventProducts, setEventProducts] = useState([]);
  const [components, setComponents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(Boolean(productId));
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [toast, setToast] = useFlashToast();
  const [duplicates, setDuplicates] = useState(null);

  // Media
  const [uploading, setUploading] = useState({ image: false, pdf: false, model: false });
  const [progress, setProgress] = useState({ pdf: 0, model: 0 });
  const [mediaErrors, setMediaErrors] = useState([]);
  const [pdfUrl, setPdfUrl] = useState('');
  const [modelUrl, setModelUrl] = useState('');
  const [fileInputKey, setFileInputKey] = useState(0);
  // Storage objects to delete after the next successful save (replaced or removed files)
  const [obsoletePaths, setObsoletePaths] = useState([]);
  const newPrefixRef = useRef(newAssetPrefix());

  // SKU suggestion from the booth number (create only, while the SKU field was not edited). The product
  // name is never derived from the booth number: it is the commercial name on the order and invoice.
  const autoFillRef = useRef({ sku: true });

  // Events card
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [eventBusy, setEventBusy] = useState(null);
  const [eventErrors, setEventErrors] = useState([]);
  const [eventProductToRemove, setEventProductToRemove] = useState(null);
  const [savedEventValues, setSavedEventValues] = useState({});

  // Offer rules (booth offering model): match rule (Product.boothMatchRule) and price model
  // (Product.boothPriceMode + boothPriceRule) of this product, edited as form state
  const [matchRuleForm, setMatchRuleForm] = useState({ ...EMPTY_MATCH_RULE_FORM, invalid: null });
  const [priceRuleForm, setPriceRuleForm] = useState({ ...EMPTY_PRICE_RULE_FORM, invalid: null });
  // preview of the rules against the live hall plan of one event
  const [previewEventId, setPreviewEventId] = useState(null);
  const [previewReload, setPreviewReload] = useState(0);
  const [preview, setPreview] = useState({
    eventId: null, data: null, loading: false, error: null,
  });

  const updateForm = (changes) => setForm((prev) => ({ ...prev, ...changes }));

  // rule JSON as it would be saved (validated with utils/boothMap via utils/boothOfferAdmin)
  const matchOut = useMemo(() => matchRuleFromForm(matchRuleForm), [matchRuleForm]);
  const priceOut = useMemo(() => priceRuleFromForm(priceRuleForm), [priceRuleForm]);
  const updateMatchRule = (changes) => setMatchRuleForm((prev) => ({ ...prev, ...changes }));
  const updatePriceRule = (changes) => setPriceRuleForm((prev) => ({ ...prev, ...changes }));
  const updateSurcharge = (index, changes) => setPriceRuleForm((prev) => ({
    ...prev,
    surcharges: prev.surcharges.map((row, i) => (i === index ? { ...row, ...changes } : row)),
  }));

  // EventProduct of this product in the preview event (base price, per-event price rule, time tiers)
  const previewLinkedEp = useMemo(
    () => eventProducts.find((item) => item.event?.id && item.event.id === previewEventId) || null,
    [eventProducts, previewEventId],
  );
  const previewData = preview.eventId === previewEventId ? preview.data : null;
  const featureKeys = useMemo(() => areaFeatureKeys(previewData?.areas || []), [previewData]);
  const rulePreview = useMemo(() => {
    if (!previewData) return null;
    const linkedPrice = previewLinkedEp ? parseDecimal(previewLinkedEp.price) : null;
    const variant = cleanCode(form.boothVariant);
    return offerRulePreview({
      areas: previewData.areas,
      eventProducts: previewData.eventProducts,
      draft: {
        productId: productId || null,
        eventProductId: previewLinkedEp?.id || null,
        createdAt: previewLinkedEp?.createdAt || null,
        boothNumber: cleanCode(form.boothNumber),
        boothVariant: variant,
        boothVariantAliases: variant ? parseAliasText(form.boothVariantAliases, variant) : null,
        boothMatchRule: matchOut.json,
        product: {
          price: Number.isFinite(parseDecimal(form.price)) ? parseDecimal(form.price) : null,
          boothPriceMode: priceOut.priceMode,
          boothPriceRule: priceOut.json,
          boothArea: Number.isFinite(parseDecimal(form.boothArea)) ? parseDecimal(form.boothArea) : null,
        },
        eventProduct: previewLinkedEp ? {
          price: Number.isFinite(linkedPrice) ? linkedPrice : null,
          boothPriceRule: previewLinkedEp.boothPriceRule ?? null,
          timeRangePrices: previewLinkedEp.timeRangePrices || null,
        } : null,
      },
    });
  }, [previewData, previewLinkedEp, productId, form.boothNumber, form.boothVariant, form.boothVariantAliases, form.price, form.boothArea, matchOut, priceOut]);

  // ----- Loading -----------------------------------------------------------

  const loadCategories = useCallback(async () => {
    try {
      const items = await fetchAllPages(LIST_CATEGORIES_QUERY, 'listProductCategories', { limit: 500 });
      setCategories(items.sort((a, b) => (a.sortKey ?? 0) - (b.sortKey ?? 0)));
    } catch (error) {
      console.warn('Error loading product categories', error);
      captureError(error, {
        feature: FEATURE,
        operation: 'fetch-categories',
        consoleMessage: 'Error loading product categories',
        tags: { component: COMPONENT },
      });
    }
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      const items = await fetchAllPages(LIST_FAIR_EVENTS_QUERY, 'listEvents', { limit: 200 });
      setEvents(items.sort((a, b) => new Date(b.startDate || 0) - new Date(a.startDate || 0)));
    } catch (error) {
      console.warn('Error loading events', error);
      captureError(error, {
        feature: FEATURE,
        operation: 'fetch-events',
        consoleMessage: 'Error loading events',
        tags: { component: COMPONENT },
      });
    }
  }, []);

  const loadProduct = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const response = await graphqlAllowPartial(
        { query: GET_BOOTH_QUERY, variables: { id: productId } },
        'getProduct',
        'fetch-booth'
      );
      const product = response.data.getProduct;
      if (!product) {
        setLoadError('Booth not found.');
        return;
      }
      setForm({
        boothNumber: product.boothNumber || '',
        boothVariant: product.boothVariant || '',
        boothVariantAliases: (Array.isArray(product.boothVariantAliases) ? product.boothVariantAliases : [])
          .filter(Boolean)
          .join(', '),
        type: product.type || 'event_booth',
        name: toLocalizedState(product.name),
        invoiceName: toLocalizedState(product.invoiceName),
        sku: product.sku || '',
        price: numberToInput(product.price),
        boothArea: numberToInput(product.boothArea),
        boothWidth: numberToInput(product.boothWidth),
        boothDepth: numberToInput(product.boothDepth),
        boothHeight: numberToInput(product.boothHeight),
        visibleInShop: Boolean(product.visibleInShop),
        allowedBuyerGroups: Array.isArray(product.allowedBuyerGroups) ? product.allowedBuyerGroups.filter(Boolean) : [],
        productCategoryProductsId: product.productCategoryProductsId || null,
        image: cleanMultiImage(product.image),
        boothDimensionFile: cleanFile(product.boothDimensionFile),
        booth3DModel: cleanFile(product.booth3DModel),
      });
      setMatchRuleForm(matchRuleToForm(product.boothMatchRule));
      setPriceRuleForm(priceRuleToForm(product.boothPriceMode, product.boothPriceRule));
      setObsoletePaths([]);
      setIncludesRows(includesToRows(product.includes));
      const sortedEventProducts = sortEventProducts((product.eventProducts?.items || []).filter(Boolean));
      setEventProducts(sortedEventProducts);
      // preview with the most recent event of the booth (the admin can pick another one)
      setPreviewEventId((prev) => prev || sortedEventProducts.find((item) => item.event?.id)?.event?.id || null);
      setSavedEventValues(
        Object.fromEntries(
          sortedEventProducts.map((item) => [item.id, { price: item.price ?? null, stock: item.stock ?? null }])
        )
      );
      setComponents(
        (product.components?.items || []).filter(Boolean).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      );
    } catch (error) {
      console.warn('Error loading booth', error);
      captureError(error, {
        feature: FEATURE,
        operation: 'fetch-booth',
        consoleMessage: 'Error loading booth',
        context: { productId },
        tags: { component: COMPONENT },
      });
      setLoadError('The booth could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // events: "Add to event" (edit) and the preview of the offer rules (create and edit)
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    if (productId) {
      setPreviewEventId(null);
      loadProduct();
    } else {
      setForm(EMPTY_FORM);
      setMatchRuleForm({ ...EMPTY_MATCH_RULE_FORM, invalid: null });
      setPriceRuleForm({ ...EMPTY_PRICE_RULE_FORM, invalid: null });
      setPreviewEventId(null);
      setIncludesRows([]);
      setEventProducts([]);
      setComponents([]);
      setObsoletePaths([]);
      autoFillRef.current = { sku: true };
      newPrefixRef.current = newAssetPrefix();
    }
    setErrors([]);
    setMediaErrors([]);
  }, [productId, loadProduct]);

  // Live areas + booth offers of the preview event
  useEffect(() => {
    if (!previewEventId) {
      setPreview({
        eventId: null, data: null, loading: false, error: null,
      });
      return undefined;
    }
    let cancelled = false;
    setPreview((prev) => ({
      ...prev, eventId: previewEventId, loading: true, error: null,
    }));
    loadPreviewData(previewEventId)
      .then((data) => {
        if (!cancelled) {
          setPreview({
            eventId: previewEventId, data, loading: false, error: null,
          });
        }
      })
      .catch((error) => {
        console.warn('Error loading the hall plan preview', error);
        captureError(error, {
          feature: FEATURE,
          operation: 'fetch-offer-rule-preview',
          consoleMessage: 'Error loading the hall plan of the preview event',
          context: { productId, eventId: previewEventId },
          tags: { component: COMPONENT },
        });
        if (!cancelled) {
          setPreview({
            eventId: previewEventId, data: null, loading: false, error: 'The hall plan of the event could not be loaded.',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [previewEventId, previewReload, productId]);

  // Signed URLs for the dimension PDF and the 3D model
  const pdfPath = form.boothDimensionFile?.s3Path || '';
  const modelPath = form.booth3DModel?.s3Path || '';

  useEffect(() => {
    let cancelled = false;
    setPdfUrl('');
    if (!pdfPath) return undefined;
    getBoothAssetUrl(pdfPath)
      .then((url) => {
        if (!cancelled) setPdfUrl(url);
      })
      .catch((error) => {
        captureError(error, {
          feature: FEATURE,
          operation: 'get-dimension-file-url',
          consoleMessage: 'Error getting the dimension drawing URL',
          context: { s3Path: pdfPath },
          tags: { component: COMPONENT },
        });
      });
    return () => {
      cancelled = true;
    };
  }, [pdfPath]);

  useEffect(() => {
    let cancelled = false;
    setModelUrl('');
    if (!modelPath) return undefined;
    getBoothAssetUrl(modelPath)
      .then((url) => {
        if (!cancelled) setModelUrl(url);
      })
      .catch((error) => {
        captureError(error, {
          feature: FEATURE,
          operation: 'get-3d-model-url',
          consoleMessage: 'Error getting the 3D model URL',
          context: { s3Path: modelPath },
          tags: { component: COMPONENT },
        });
      });
    return () => {
      cancelled = true;
    };
  }, [modelPath]);

  // ----- Form handlers -----------------------------------------------------

  // Only the SKU is suggested ('CP-ST-' + normalized booth number or variant code) while the admin has not
  // typed an SKU; without either nothing is suggested. The product name is always typed by the admin.
  const handleCodeChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (!isEdit && autoFillRef.current.sku) {
        next.sku = suggestedSku(next.boothNumber, next.boothVariant);
      }
      return next;
    });
  };

  const handleNameChange = (language, text) => {
    setForm((prev) => ({ ...prev, name: setLocalized(prev.name, language, text) }));
  };

  const handleInvoiceNameChange = (language, text) => {
    setForm((prev) => ({ ...prev, invoiceName: setLocalized(prev.invoiceName, language, text) }));
  };

  const handleSkuChange = (value) => {
    autoFillRef.current.sku = value.trim() === '';
    updateForm({ sku: value });
  };

  const toggleBuyerGroup = (group, checked) => {
    setForm((prev) => {
      const current = prev.allowedBuyerGroups || [];
      const nextGroups = checked ? [...current.filter((g) => g !== group), group] : current.filter((g) => g !== group);
      return { ...prev, allowedBuyerGroups: nextGroups };
    });
  };

  const updateIncludeRow = (index, language, text) => {
    setIncludesRows((prev) => prev.map((row, i) => (i === index ? { ...row, [language]: text } : row)));
  };

  // ----- Media -------------------------------------------------------------

  const assetPrefix = productId ? `booth-assets/${productId}` : newPrefixRef.current;

  const markObsolete = (s3Path) => {
    if (!s3Path) return;
    setObsoletePaths((prev) => (prev.includes(s3Path) ? prev : [...prev, s3Path]));
  };

  const handleImageFile = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type || '')) {
      setMediaErrors(['Please choose an image file (PNG or JPG).']);
      return;
    }
    setMediaErrors([]);
    setUploading((prev) => ({ ...prev, image: true }));
    try {
      const imageBuffer = await readFileAsBase64(file);
      const response = await client.graphql({
        query: UPLOAD_PRODUCT_IMAGE_MUTATION,
        variables: { imageBuffer, imageType: 'productImage' },
      });
      const fileName = parseImageUploadResult(response.data.uploadCompanyImage);
      const alt = getLanguageText(formRef.current.name, 'de')
        || formRef.current.boothNumber.trim()
        || formRef.current.boothVariant.trim()
        || 'Booth';
      updateForm({ image: cleanMultiImage({ ...(formRef.current.image || {}), main: { fileName, alt } }) });
    } catch (error) {
      console.warn('Error uploading booth image', error);
      captureError(error, {
        feature: FEATURE,
        operation: 'upload-booth-image',
        consoleMessage: 'Error uploading booth image',
        context: { productId, file: file.name },
        tags: { component: COMPONENT },
      });
      setMediaErrors(['The image could not be uploaded.']);
    } finally {
      setUploading((prev) => ({ ...prev, image: false }));
      setFileInputKey((key) => key + 1);
    }
  };

  const handleAssetFile = async (file, kind) => {
    if (!file) return;
    const isPdf = kind === 'pdf';
    const extensionOk = isPdf ? /\.pdf$/i.test(file.name) : /\.glb$/i.test(file.name);
    if (!extensionOk) {
      setMediaErrors([isPdf ? 'Please choose a PDF file.' : 'Please choose a GLB file.']);
      return;
    }
    setMediaErrors([]);
    setUploading((prev) => ({ ...prev, [kind]: true }));
    setProgress((prev) => ({ ...prev, [kind]: 0 }));
    try {
      const stored = await uploadBoothAsset(file, {
        keyPrefix: assetPrefix,
        contentType: isPdf ? 'application/pdf' : 'model/gltf-binary',
        onProgress: (percent) => setProgress((prev) => ({ ...prev, [kind]: percent })),
      });
      const field = isPdf ? 'boothDimensionFile' : 'booth3DModel';
      markObsolete(formRef.current[field]?.s3Path);
      updateForm({ [field]: stored });
    } catch (error) {
      console.warn('Error uploading booth file', error);
      captureError(error, {
        feature: FEATURE,
        operation: isPdf ? 'upload-dimension-file' : 'upload-3d-model',
        consoleMessage: 'Error uploading booth file',
        context: { productId, file: file.name, keyPrefix: assetPrefix },
        tags: { component: COMPONENT },
      });
      setMediaErrors(['The file could not be uploaded.']);
    } finally {
      setUploading((prev) => ({ ...prev, [kind]: false }));
      setFileInputKey((key) => key + 1);
    }
  };

  const removeAsset = (field) => {
    markObsolete(form[field]?.s3Path);
    updateForm({ [field]: null });
  };

  /** Deletes replaced / removed files once the product no longer references them. */
  const cleanupObsoleteFiles = async (keepPaths) => {
    const toDelete = obsoletePaths.filter((path) => path && !keepPaths.includes(path));
    for (const path of toDelete) {
      try {
        await removeBoothAsset(path);
      } catch (error) {
        captureError(error, {
          feature: FEATURE,
          operation: 'remove-booth-file',
          consoleMessage: 'Error removing an obsolete booth file',
          context: { productId, s3Path: path },
          tags: { component: COMPONENT },
        });
      }
    }
    setObsoletePaths([]);
  };

  // ----- Save --------------------------------------------------------------

  const validate = () => {
    const found = [];
    const hasNumber = Boolean(cleanCode(form.boothNumber));
    const variant = cleanCode(form.boothVariant);
    const aliases = parseAliasText(form.boothVariantAliases, variant) || [];
    const ruleEnabled = Boolean(matchRuleForm.enabled);
    if (!hasNumber && !variant && !ruleEnabled) found.push('Please enter a booth number, an offer code or a match rule.');
    if (hasNumber && ruleEnabled) {
      found.push('A single booth (booth number) has no match rule: remove the booth number or switch the match rule off.');
    }
    matchOut.errors.forEach((message) => found.push(`${t('Match rule')}: ${message}`));
    priceOut.errors.forEach((message) => found.push(`${t('Price rule')}: ${message}`));
    if (hasNumber && checkBoothNumberInput(form.boothNumber).error === 'reserved-id') {
      found.push('Booth numbers must not start with "#" (reserved for unnamed shapes).');
    }
    if (hasNumber && variant) {
      found.push('A booth product has either a booth number (single booth) or an offer code (all booths with this plan code), not both.');
    }
    if (variant && !isValidVariantCode(variant)) {
      found.push('The offer code may only contain letters, digits, ".", "_" and "-" (max. 16 characters).');
    }
    if (aliases.length > 0 && !variant) found.push('Aliases are only possible for an offer code.');
    if (aliases.some((alias) => !isValidVariantCode(alias))) {
      found.push('Every alias may only contain letters, digits, ".", "_" and "-" (max. 16 characters).');
    }
    if (!getLanguageText(form.name, 'de').trim()) found.push('Please enter a German name.');
    if (!form.sku.trim()) found.push('Please enter an SKU.');
    if (!BOOTH_PRODUCT_TYPES.includes(form.type)) found.push('Please select a type.');
    const price = parseDecimal(form.price);
    if (price === null || Number.isNaN(price) || price < 0) found.push('Please enter a valid price.');
    const dimensions = [form.boothArea, form.boothWidth, form.boothDepth, form.boothHeight].map(parseDecimal);
    if (dimensions.some((n) => Number.isNaN(n) || (n !== null && n < 0))) {
      found.push('Please enter valid numbers for the booth dimensions.');
    }
    return found;
  };

  // booth numbers only: variant codes are not unique (the same code exists for products of other events;
  // duplicates per event are refused in the hall plan tab of the event)
  const findDuplicateBooths = async () => {
    const key = normalizeBoothNumber(form.boothNumber);
    if (!key) return [];
    try {
      const lists = await Promise.all(
        BOOTH_PRODUCT_TYPES.map((type) =>
          fetchAllPages(BOOTH_NUMBERS_BY_TYPE_QUERY, 'productsByType', { type, limit: 500 })
        )
      );
      return lists
        .flat()
        .filter((product) => product.id !== productId && normalizeBoothNumber(product.boothNumber) === key);
    } catch (error) {
      // non-blocking: the check is only a warning
      console.warn('Error checking booth number uniqueness', error);
      captureError(error, {
        feature: FEATURE,
        operation: 'check-booth-number-unique',
        consoleMessage: 'Error checking booth number uniqueness',
        context: { productId, boothNumber: form.boothNumber },
        tags: { component: COMPONENT },
      });
      return [];
    }
  };

  const buildInput = () => {
    const name = toLocalizedInput(form.name);
    const invoiceName = toLocalizedInput(form.invoiceName);
    return {
      ...(isEdit ? { id: productId } : { icon: 'store', stock: 1 }),
      sku: form.sku.trim(),
      name,
      invoiceName: invoiceName.length > 0 ? invoiceName : null,
      type: form.type,
      price: parseDecimal(form.price),
      visibleInShop: Boolean(form.visibleInShop),
      allowedBuyerGroups: form.allowedBuyerGroups || [],
      productCategoryProductsId: form.productCategoryProductsId || null,
      includes: rowsToIncludes(includesRows),
      image: cleanMultiImage(form.image),
      // number XOR variant (validated); empty values are stored as null, never ''
      boothNumber: cleanCode(form.boothNumber),
      boothVariant: cleanCode(form.boothVariant),
      boothVariantAliases: cleanCode(form.boothVariant) ? parseAliasText(form.boothVariantAliases, form.boothVariant) : null,
      boothArea: parseDecimal(form.boothArea),
      boothWidth: parseDecimal(form.boothWidth),
      boothDepth: parseDecimal(form.boothDepth),
      boothHeight: parseDecimal(form.boothHeight),
      boothDimensionFile: cleanFile(form.boothDimensionFile),
      booth3DModel: cleanFile(form.booth3DModel),
      // booth offering model (AWSJSON as JSON strings; null = no rule, price mode null = fixed)
      boothMatchRule: matchOut.json,
      boothPriceMode: priceOut.priceMode,
      boothPriceRule: priceOut.json,
    };
  };

  const persist = async () => {
    setDuplicates(null);
    setSaving(true);
    setErrors([]);
    const input = buildInput();
    try {
      // Stock is not sent on update: placeOrder may change it concurrently
      const response = await client.graphql({
        query: isEdit ? UPDATE_PRODUCT_MUTATION : CREATE_PRODUCT_MUTATION,
        variables: { input },
      });
      const savedId = isEdit ? productId : response.data.createProduct.id;
      const keepPaths = [input.boothDimensionFile?.s3Path, input.booth3DModel?.s3Path].filter(Boolean);
      await cleanupObsoleteFiles(keepPaths);
      if (isEdit) {
        setToast('Booth saved.');
      } else {
        navigate(`/admin/booth/${savedId}`, { replace: true, state: { toast: 'Booth created.' } });
      }
    } catch (error) {
      console.warn('Error saving booth', error);
      captureError(error, {
        feature: FEATURE,
        operation: isEdit ? 'update-booth' : 'create-booth',
        consoleMessage: 'Error saving booth',
        context: { productId, input },
        tags: { component: COMPONENT },
      });
      setErrors(['The booth could not be saved.']);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const found = validate();
    setErrors(found);
    if (found.length > 0) return;
    setSaving(true);
    const others = await findDuplicateBooths();
    setSaving(false);
    if (others.length > 0) {
      setDuplicates(others);
      return;
    }
    await persist();
  };

  // ----- Events ------------------------------------------------------------

  const linkedEventIds = useMemo(
    () => new Set(eventProducts.map((item) => item.event?.id).filter(Boolean)),
    [eventProducts]
  );

  const availableEvents = useMemo(
    () => events.filter((event) => !linkedEventIds.has(event.id)),
    [events, linkedEventIds]
  );

  const reportEventError = (error, operation, context, message) => {
    console.warn('Error changing booth event link', error);
    captureError(error, {
      feature: FEATURE,
      operation,
      consoleMessage: 'Error changing booth event link',
      context: { productId, ...context },
      tags: { component: COMPONENT },
    });
    setEventErrors([message]);
  };

  const addToEvent = async () => {
    if (!selectedEventId || !productId) return;
    setEventBusy('add');
    setEventErrors([]);
    try {
      const descriptor = {
        type: form.type,
        boothNumber: form.boothNumber,
        boothVariant: form.boothVariant,
        boothVariantAliases: parseAliasText(form.boothVariantAliases, form.boothVariant) || [],
      };
      const isVariant = isVariantProduct(descriptor);
      // offers (offer code or match rule) are booked per booth of the hall plan: their stock is unused
      const isOffer = isVariant || (!normalizeBoothNumber(form.boothNumber) && Boolean(matchOut.json));
      if (isVariant) {
        const codes = variantCodesOf(descriptor);
        const conflicts = await findEventVariantConflicts(selectedEventId, productId, codes);
        if (conflicts.length > 0) {
          const conflictCodes = new Set(conflicts.flatMap((p) => variantCodesOf(p)));
          setEventErrors([t('Offer code {{codes}} is already used in this event by {{products}}. One offer per code and event.', {
            codes: codes.filter((code) => conflictCodes.has(code)).join(', '),
            products: conflicts.map((p) => p.sku || p.boothVariant || p.id).join(', '),
          })]);
          return;
        }
      }
      const price = parseDecimal(form.price);
      const response = await client.graphql({
        query: CREATE_EVENT_PRODUCT_MUTATION,
        variables: {
          input: {
            eventEventProductsId: selectedEventId,
            productEventProductsId: productId,
            price: Number.isFinite(price) ? price : null,
            // 1 per single booth; offers are booked per booth, their stock is unused (counted by the hall plan tab)
            stock: isOffer ? null : 1,
            isAvailable: true,
          },
        },
      });
      const created = response.data.createEventProduct;
      setEventProducts((prev) => sortEventProducts([...prev, created]));
      setSavedEventValues((prev) => ({ ...prev, [created.id]: { price: created.price ?? null, stock: created.stock ?? null } }));
      setSelectedEventId(null);
      // the preview follows the new link (its offers changed)
      setPreviewEventId((prev) => prev || created.event?.id || null);
      setPreviewReload((value) => value + 1);
    } catch (error) {
      reportEventError(error, 'add-booth-to-event', { eventId: selectedEventId }, 'The booth could not be added to the event.');
    } finally {
      setEventBusy(null);
    }
  };

  const updateEventProduct = async (eventProductId, changes) => {
    setEventBusy(eventProductId);
    setEventErrors([]);
    try {
      const response = await client.graphql({
        query: UPDATE_EVENT_PRODUCT_MUTATION,
        variables: { input: { id: eventProductId, ...changes } },
      });
      const updated = response.data.updateEventProduct;
      setEventProducts((prev) => prev.map((item) => (item.id === eventProductId ? { ...item, ...updated } : item)));
      setSavedEventValues((prev) => ({
        ...prev,
        [eventProductId]: { price: updated.price ?? null, stock: updated.stock ?? null },
      }));
    } catch (error) {
      reportEventError(error, 'update-booth-event-product', { eventProductId, changes }, 'The event link could not be updated.');
      // restore the last saved values
      const saved = savedEventValues[eventProductId];
      if (saved) {
        setEventProducts((prev) => prev.map((item) => (item.id === eventProductId ? { ...item, ...saved } : item)));
      }
    } finally {
      setEventBusy(null);
    }
  };

  const editEventValue = (eventProductId, field, value) => {
    setEventProducts((prev) => prev.map((item) => (item.id === eventProductId ? { ...item, [field]: value } : item)));
  };

  const commitEventValue = (eventProduct, field) => {
    const raw = eventProduct[field];
    const saved = savedEventValues[eventProduct.id]?.[field] ?? null;
    let value = parseDecimal(raw);
    if (field === 'stock' && value !== null && !Number.isNaN(value)) value = Math.trunc(value);
    if (Number.isNaN(value) || (value !== null && value < 0) || (field === 'stock' && value === null)) {
      setEventErrors([field === 'stock' ? 'Please enter a valid stock.' : 'Please enter a valid price.']);
      editEventValue(eventProduct.id, field, saved);
      return;
    }
    if (value === saved) return;
    updateEventProduct(eventProduct.id, { [field]: value });
  };

  const confirmRemoveEventProduct = async () => {
    if (!eventProductToRemove) return;
    setEventBusy(eventProductToRemove.id);
    setEventErrors([]);
    try {
      await client.graphql({ query: DELETE_EVENT_PRODUCT_MUTATION, variables: { input: { id: eventProductToRemove.id } } });
      const removedId = eventProductToRemove.id;
      setEventProducts((prev) => prev.filter((item) => item.id !== removedId));
      setEventProductToRemove(null);
    } catch (error) {
      reportEventError(error, 'remove-booth-from-event', { eventProductId: eventProductToRemove.id }, 'The booth could not be removed from the event.');
      setEventProductToRemove(null);
    } finally {
      setEventBusy(null);
    }
  };

  // ----- Render ------------------------------------------------------------

  const busyUploading = uploading.image || uploading.pdf || uploading.model;
  const previewWidth = parseDecimal(form.boothWidth);
  const previewDepth = parseDecimal(form.boothDepth);
  const previewHeight = parseDecimal(form.boothHeight);
  const imageUrl = boothImageUrl(form.image?.main?.fileName);
  const boothNumberCheck = checkBoothNumberInput(form.boothNumber);

  if (loading) {
    return (
      <div className="text-center my-5">
        <MDBSpinner role="status" />
      </div>
    );
  }

  if (loadError) {
    return (
      <MDBCard>
        <MDBCardBody>
          <ErrorList errors={[loadError]} />
          <Link to="/admin/booths">
            <MDBBtn color="secondary" outline>
              {t('Back to booths')}
            </MDBBtn>
          </Link>
        </MDBCardBody>
      </MDBCard>
    );
  }

  return (
    <>
      <FlashToast message={toast} onClose={() => setToast(null)} />

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <h4 className="mb-0">
          {isEdit
            ? t('Edit booth {{boothNumber}}', { boothNumber: form.boothNumber.trim() || form.boothVariant.trim() || '-' })
            : t('Create booth')}
        </h4>
        <div className="d-flex flex-wrap gap-2">
          <Link to="/admin/booths">
            <MDBBtn color="secondary" outline>
              {t('Back to booths')}
            </MDBBtn>
          </Link>
          {isEdit && (
            <Link to={`/admin/product/${productId}`}>
              <MDBBtn color="secondary" outline>
                {t('Edit as product')}
              </MDBBtn>
            </Link>
          )}
          <MDBBtn color="primary" onClick={handleSave} disabled={saving || busyUploading}>
            {saving && <MDBSpinner size="sm" className="me-2" />}
            {t('Save')}
          </MDBBtn>
        </div>
      </div>

      <ErrorList errors={errors} />

      <MDBRow className="g-3">
        {/* ------------------------------------------------------------ Booth data */}
        <MDBCol lg="7">
          <MDBCard className="h-100">
            <MDBCardBody>
              <MDBCardTitle>{t('Booth data')}</MDBCardTitle>
              <div className="mt-3">
                <LocalizedInput
                  value={form.name}
                  language="de"
                  label={t('Product name (as on the order and invoice)')}
                  required
                  onChange={(text) => handleNameChange('de', text)}
                />
                <LocalizedInput
                  value={form.name}
                  language="en"
                  label={t('Product name (as on the order and invoice)')}
                  onChange={(text) => handleNameChange('en', text)}
                />
                <LocalizedInput
                  value={form.invoiceName}
                  language="de"
                  label={t('Invoice name (optional, defaults to the product name)')}
                  onChange={(text) => handleInvoiceNameChange('de', text)}
                />
                <LocalizedInput
                  value={form.invoiceName}
                  language="en"
                  label={t('Invoice name (optional, defaults to the product name)')}
                  onChange={(text) => handleInvoiceNameChange('en', text)}
                />
              </div>

              <MDBRow className="g-3">
                <MDBCol md="6">
                  <MDBInput
                    label={t('Booth number (hall plan)')}
                    type="text"
                    value={form.boothNumber}
                    onChange={(e) => handleCodeChange('boothNumber', e.target.value)}
                  />
                  <div className="form-text">
                    {t('Only the number as printed on the hall plan, any scheme (e.g. A1.1, A-12, H3/B12). The product name stays the commercial name.')}
                  </div>
                  {boothNumberCheck.warnings.includes('id-whitespace') && (
                    <div className="form-text text-warning">{t('The booth number contains spaces; it is matched with collapsed spaces and must be written exactly like that in the SVG.')}</div>
                  )}
                  {boothNumberCheck.warnings.includes('id-too-long') && (
                    <div className="form-text text-warning">{t('The booth number is longer than 32 characters.')}</div>
                  )}
                  {boothNumberCheck.error === 'reserved-id' && (
                    <div className="form-text text-danger">{t('Booth numbers must not start with "#" (reserved for unnamed shapes).')}</div>
                  )}
                </MDBCol>
                <MDBCol md="6">
                  <MDBInput
                    label={t('Offer code (hall plan)')}
                    type="text"
                    value={form.boothVariant}
                    onChange={(e) => handleCodeChange('boothVariant', e.target.value)}
                  />
                  <div className="form-text">
                    {t('Either a booth number (one physical booth) or an offer code, e.g. 18B: one offer for all booths of the hall plan with this plan code (data-variant); the exhibitor picks the booth number.')}
                  </div>
                </MDBCol>
                <MDBCol md="6">
                  <MDBInput
                    label={t('Offer code aliases (comma-separated)')}
                    type="text"
                    value={form.boothVariantAliases}
                    disabled={!form.boothVariant.trim()}
                    onChange={(e) => updateForm({ boothVariantAliases: e.target.value })}
                  />
                  <div className="form-text">
                    {t('Other plan codes meaning this offer, e.g. 9 for Z9. Used for matching only.')}
                  </div>
                </MDBCol>
                <MDBCol md="6">
                  <MDBSelect
                    label={`${t('Type')} *`}
                    value={form.type}
                    onChange={(entry) => updateForm({ type: entry?.value || 'event_booth' })}
                    data={[
                      { text: t('Event booths'), value: 'event_booth' },
                      { text: t('Event spaces'), value: 'event_space' },
                    ]}
                  />
                </MDBCol>
              </MDBRow>

              <MDBRow className="g-3 mt-1">
                <MDBCol md="6">
                  <MDBInput
                    label={`${t('SKU')} *`}
                    type="text"
                    value={form.sku}
                    onChange={(e) => handleSkuChange(e.target.value)}
                  />
                </MDBCol>
                <MDBCol md="6">
                  <MDBInput
                    label={`${priceRuleForm.mode === 'perSqm' ? t('Base price per m²') : t('Price')} *`}
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.price}
                    onChange={(e) => updateForm({ price: e.target.value })}
                  >
                    <i className="fas fa-euro-sign trailing" />
                  </MDBInput>
                  <div className="form-text">
                    {priceRuleForm.mode === 'perSqm'
                      ? t('Default rate per m²; each event can override it. The booth price is rate × m² of the booth plus surcharges.')
                      : t('Default price; each event can override it.')}
                  </div>
                </MDBCol>
              </MDBRow>

              <h6 className="mt-4 mb-3">{t('Size')}</h6>
              <MDBRow className="g-3">
                <MDBCol sm="6" md="3">
                  <MDBInput
                    label={t('Area (m²)')}
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.boothArea}
                    onChange={(e) => updateForm({ boothArea: e.target.value })}
                  />
                </MDBCol>
                <MDBCol sm="6" md="3">
                  <MDBInput
                    label={t('Width (m)')}
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.boothWidth}
                    onChange={(e) => updateForm({ boothWidth: e.target.value })}
                  />
                </MDBCol>
                <MDBCol sm="6" md="3">
                  <MDBInput
                    label={t('Depth (m)')}
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.boothDepth}
                    onChange={(e) => updateForm({ boothDepth: e.target.value })}
                  />
                </MDBCol>
                <MDBCol sm="6" md="3">
                  <MDBInput
                    label={t('Height (m)')}
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.boothHeight}
                    onChange={(e) => updateForm({ boothHeight: e.target.value })}
                  />
                </MDBCol>
              </MDBRow>

              <h6 className="mt-4 mb-2">{t('Included in the package')}</h6>
              <p className="small text-muted mb-3" data-testid="includes-components-hint">
                <MDBIcon fas icon="info-circle" className="me-1" />
                {t('Components (equipment products with quantities, product editor tab Components) are listed to exhibitors automatically. Use this list only for services that are not components, e.g. programme participation or CP-COMPANY.')}
              </p>
              {includesRows.length === 0 && <p className="text-muted small">{t('No included items yet.')}</p>}
              {includesRows.map((row, index) => (
                <MDBRow className="g-2 mb-2 align-items-start" key={index}>
                  <MDBCol size="12" md="5" className="d-flex gap-2">
                    <FlagLabel language="de" />
                    <MDBTextArea
                      label={t('Included item')}
                      rows={2}
                      value={row.de}
                      onChange={(e) => updateIncludeRow(index, 'de', e.target.value)}
                    />
                  </MDBCol>
                  <MDBCol size="12" md="5" className="d-flex gap-2">
                    <FlagLabel language="en" />
                    <MDBTextArea
                      label={t('Included item')}
                      rows={2}
                      value={row.en}
                      onChange={(e) => updateIncludeRow(index, 'en', e.target.value)}
                    />
                  </MDBCol>
                  <MDBCol size="12" md="2">
                    <MDBBtn
                      color="danger"
                      outline
                      size="sm"
                      className="mt-1"
                      title={t('Remove')}
                      aria-label={t('Remove')}
                      onClick={() => setIncludesRows((prev) => prev.filter((_, i) => i !== index))}
                    >
                      <MDBIcon fas icon="trash-alt" />
                    </MDBBtn>
                  </MDBCol>
                </MDBRow>
              ))}
              <MDBBtn
                color="primary"
                outline
                size="sm"
                onClick={() => setIncludesRows((prev) => [...prev, { de: '', en: '' }])}
              >
                <MDBIcon fas icon="plus" className="me-2" />
                {t('Add included item')}
              </MDBBtn>

              <h6 className="mt-4 mb-3">{t('Shop settings')}</h6>
              <MDBRow className="g-3">
                <MDBCol md="4">
                  <MDBSwitch
                    id="boothVisibleInShop"
                    label={t('Visible in shop')}
                    checked={Boolean(form.visibleInShop)}
                    onChange={(e) => updateForm({ visibleInShop: e.target.checked })}
                  />
                </MDBCol>
                <MDBCol md="4">
                  <span className="fw-bold d-block mb-1">{t('Allowed buyer groups')}</span>
                  <MDBCheckbox
                    id="boothBuyerConsumer"
                    label={t('Consumer')}
                    checked={(form.allowedBuyerGroups || []).includes('consumer')}
                    onChange={(e) => toggleBuyerGroup('consumer', e.target.checked)}
                  />
                  <MDBCheckbox
                    id="boothBuyerBusiness"
                    label={t('Business')}
                    checked={(form.allowedBuyerGroups || []).includes('business')}
                    onChange={(e) => toggleBuyerGroup('business', e.target.checked)}
                  />
                </MDBCol>
                <MDBCol md="4">
                  <MDBSelect
                    label={t('Category')}
                    value={form.productCategoryProductsId}
                    onChange={(entry) => updateForm({ productCategoryProductsId: entry?.value || null })}
                    data={[
                      { text: t('Select Category'), value: null },
                      ...categories.map((category) => ({ text: getLocalizedText(category.name), value: category.id })),
                    ]}
                  />
                </MDBCol>
              </MDBRow>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>

        {/* ------------------------------------------------------------ Media */}
        <MDBCol lg="5">
          <MDBCard className="h-100">
            <MDBCardBody>
              <MDBCardTitle>{t('Media')}</MDBCardTitle>
              <ErrorList errors={mediaErrors} />

              {/* Image */}
              <h6 className="mt-3">{t('Booth image')}</h6>
              {imageUrl ? (
                <div className="d-flex align-items-start gap-3 mb-2">
                  <img
                    src={imageUrl}
                    alt={form.image?.main?.alt || ''}
                    style={{ width: '160px', height: '110px', objectFit: 'contain' }}
                    className="border rounded"
                  />
                  <MDBBtn color="danger" outline size="sm" onClick={() => updateForm({ image: cleanMultiImage({ ...(form.image || {}), main: null }) })}>
                    {t('Remove')}
                  </MDBBtn>
                </div>
              ) : (
                <p className="text-muted small mb-2">{t('No image uploaded.')}</p>
              )}
              <div className="d-flex align-items-center gap-2">
                <div className="flex-grow-1">
                  <MDBFile
                    key={`image-${fileInputKey}`}
                    accept="image/png,image/jpeg"
                    disabled={uploading.image}
                    onChange={(e) => handleImageFile(e.target.files?.[0])}
                  />
                </div>
                {uploading.image && <MDBSpinner size="sm" />}
              </div>

              {/* Dimension drawing */}
              <h6 className="mt-4">{t('Dimension drawing (PDF)')}</h6>
              {form.boothDimensionFile ? (
                <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                  <MDBIcon far icon="file-pdf" className="text-danger" />
                  {pdfUrl ? (
                    <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                      {form.boothDimensionFile.fileName || t('Open PDF')}
                    </a>
                  ) : (
                    <span>{form.boothDimensionFile.fileName}</span>
                  )}
                  <MDBBtn color="danger" outline size="sm" onClick={() => removeAsset('boothDimensionFile')}>
                    {t('Remove')}
                  </MDBBtn>
                </div>
              ) : (
                <p className="text-muted small mb-2">{t('No dimension drawing uploaded.')}</p>
              )}
              <div className="d-flex align-items-center gap-2">
                <div className="flex-grow-1">
                  <MDBFile
                    key={`pdf-${fileInputKey}`}
                    accept=".pdf,application/pdf"
                    disabled={uploading.pdf}
                    onChange={(e) => handleAssetFile(e.target.files?.[0], 'pdf')}
                  />
                </div>
                {uploading.pdf && <span className="small text-muted text-nowrap">{progress.pdf} %</span>}
              </div>

              {/* 3D model */}
              <h6 className="mt-4">{t('3D model (GLB)')}</h6>
              {form.booth3DModel ? (
                <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                  <MDBIcon fas icon="cube" />
                  <span>{form.booth3DModel.fileName}</span>
                  <MDBBtn color="danger" outline size="sm" onClick={() => removeAsset('booth3DModel')}>
                    {t('Remove')}
                  </MDBBtn>
                </div>
              ) : (
                <p className="text-muted small mb-2">
                  {t('No 3D model uploaded. A simple booth box from width, depth and height is shown instead.')}
                </p>
              )}
              <div className="d-flex align-items-center gap-2">
                <div className="flex-grow-1">
                  <MDBFile
                    key={`model-${fileInputKey}`}
                    accept=".glb,model/gltf-binary"
                    disabled={uploading.model}
                    onChange={(e) => handleAssetFile(e.target.files?.[0], 'model')}
                  />
                </div>
                {uploading.model && <span className="small text-muted text-nowrap">{progress.model} %</span>}
              </div>

              <div className="mt-3">
                <div className="small text-muted mb-1">
                  {form.booth3DModel ? t('3D preview') : t('3D preview (generated from the dimensions)')}
                </div>
                {form.booth3DModel && !modelUrl ? (
                  <PreviewSpinner />
                ) : (
                  <Suspense fallback={<PreviewSpinner />}>
                    <Booth3DView
                      modelUrl={form.booth3DModel ? modelUrl : undefined}
                      width={Number.isFinite(previewWidth) ? previewWidth : undefined}
                      depth={Number.isFinite(previewDepth) ? previewDepth : undefined}
                      height={Number.isFinite(previewHeight) ? previewHeight : undefined}
                      className="border rounded"
                    />
                  </Suspense>
                )}
              </div>
              {obsoletePaths.length > 0 && (
                <p className="small text-muted mt-2 mb-0">{t('Replaced files are deleted when you save.')}</p>
              )}
            </MDBCardBody>
          </MDBCard>
        </MDBCol>

        {/* ------------------------------------------------------------ Offer: match rule + price rule */}
        <MDBCol size="12">
          <MDBCard>
            <MDBCardBody>
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                <div style={{ maxWidth: '720px' }}>
                  <MDBCardTitle>{t('Offer: match rule and price')}</MDBCardTitle>
                  <p className="small text-muted mb-0">
                    {t('Which booths of a hall plan this offer covers and how their price is calculated. Order of the assignment: a single booth number, then the plan code (offer code / aliases), then the match rule. Without a match rule only the offer code counts; without a price rule every booth costs the base price.')}
                  </p>
                </div>
                <div className="d-flex align-items-end gap-2" style={{ minWidth: '300px' }}>
                  <div className="flex-grow-1">
                    <MDBSelect
                      label={t('Preview with the hall plan of')}
                      search
                      value={previewEventId}
                      onChange={(entry) => setPreviewEventId(entry?.value || null)}
                      data={[
                        { text: t('No preview'), value: null },
                        ...events.map((event) => ({
                          text: linkedEventIds.has(event.id) ? `${getLocalizedText(event.name)} ✓` : getLocalizedText(event.name),
                          value: event.id,
                        })),
                      ]}
                    />
                  </div>
                  <MDBBtn
                    type="button"
                    size="sm"
                    color="secondary"
                    title={t('Refresh')}
                    aria-label={t('Refresh')}
                    disabled={!previewEventId || preview.loading}
                    onClick={() => setPreviewReload((value) => value + 1)}
                  >
                    {preview.loading ? <MDBSpinner size="sm" /> : <MDBIcon fas icon="sync-alt" />}
                  </MDBBtn>
                </div>
              </div>
              {preview.error && preview.eventId === previewEventId && <ErrorList errors={[preview.error]} />}

              <MDBRow className="g-4 mt-1">
                {/* ----- match rule */}
                <MDBCol lg="6">
                  <h6>{t('Match rule')}</h6>
                  <MDBSwitch
                    id="boothMatchRuleEnabled"
                    label={t('Assign booths by their features (size, position type, zone)')}
                    checked={Boolean(matchRuleForm.enabled)}
                    disabled={Boolean(cleanCode(form.boothNumber)) && !matchRuleForm.enabled}
                    onChange={(e) => updateMatchRule({ enabled: e.target.checked })}
                  />
                  {Boolean(cleanCode(form.boothNumber)) && (
                    <div className="form-text">{t('Single booths (booth number) have no match rule.')}</div>
                  )}
                  {matchRuleForm.invalid && (
                    <div className="alert alert-warning small mt-2">
                      {t('The stored match rule is invalid and matches no booth: {{errors}}. Saving replaces it with the rule below.', {
                        errors: matchRuleForm.invalid.join('; '),
                      })}
                    </div>
                  )}
                  {matchRuleForm.enabled && (
                    <div className="mt-3">
                      <MDBRow className="g-2 mb-3">
                        <MDBCol size="4">
                          <MDBInput
                            label={t('Size from (m²)')}
                            type="number"
                            min="0"
                            step="0.01"
                            size="sm"
                            value={matchRuleForm.sizeMin}
                            onChange={(e) => updateMatchRule({ sizeMin: e.target.value })}
                          />
                        </MDBCol>
                        <MDBCol size="4">
                          <MDBInput
                            label={t('Size to (m²)')}
                            type="number"
                            min="0"
                            step="0.01"
                            size="sm"
                            value={matchRuleForm.sizeMax}
                            onChange={(e) => updateMatchRule({ sizeMax: e.target.value })}
                          />
                        </MDBCol>
                        <MDBCol size="4">
                          <MDBInput
                            label={t('Priority')}
                            type="number"
                            step="1"
                            size="sm"
                            value={matchRuleForm.priority}
                            onChange={(e) => updateMatchRule({ priority: e.target.value })}
                          />
                        </MDBCol>
                        <MDBCol size="12">
                          <div className="form-text mt-0">
                            {t('Booths without a size (data-size) never match a size condition. Higher priority wins; with equal priority the rule with more conditions, then the offer linked first.')}
                          </div>
                        </MDBCol>
                      </MDBRow>
                      <KeyChips
                        idPrefix="booth-rule-position"
                        label={t('Position types')}
                        value={matchRuleForm.positionTypes}
                        suggestions={[...STANDARD_POSITION_KEYS, ...featureKeys.positionTypes]}
                        labelOf={(key) => (POSITION_LABELS[key] ? `${t(POSITION_LABELS[key])} (${key})` : key)}
                        normalize={normalizePositionKey}
                        onChange={(positionTypes) => updateMatchRule({ positionTypes })}
                      />
                      <KeyChips
                        idPrefix="booth-rule-zone"
                        label={t('Zones')}
                        value={matchRuleForm.zones}
                        suggestions={featureKeys.zones}
                        onChange={(zones) => updateMatchRule({ zones })}
                      />
                      {matchRuleForm.zones.length > 0 && (
                        <div className="form-text mt-n2 mb-2">{t('Booths without a zone do not match a rule with zones.')}</div>
                      )}
                      {matchOut.matchesAll && (
                        <div className="alert alert-info small">
                          {t('This rule has no condition: it matches every booth that is not taken by a single booth or a plan code.')}
                        </div>
                      )}
                      {cleanCode(form.boothVariant) && (
                        <div className="small text-muted mb-2">
                          {t('Booths with the plan code {{code}} are assigned by the code; the rule covers further booths.', { code: cleanCode(form.boothVariant) })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* preview: booths of the active plan */}
                  <div className="border rounded p-2 mt-2 small" data-testid="offer-rule-preview">
                    {!previewEventId && (
                      <span className="text-muted">{t('Choose an event above to see which booths of its active hall plan this offer covers.')}</span>
                    )}
                    {previewEventId && preview.loading && !rulePreview && <MDBSpinner size="sm" />}
                    {rulePreview && rulePreview.total === 0 && (
                      <span className="text-muted">{t('The event has no hall plan booths yet.')}</span>
                    )}
                    {rulePreview && rulePreview.total > 0 && (
                      <>
                        {matchRuleForm.enabled && matchOut.json && (
                          <div className="fw-bold">
                            {t('The rule matches {{count}} of {{total}} booths of the active plan.', {
                              count: rulePreview.ruleMatches.length,
                              total: rulePreview.total,
                            })}
                          </div>
                        )}
                        <div title={rulePreview.primary.join(', ')}>
                          {t('Primary offer for {{count}} booths', { count: rulePreview.primary.length })}
                          {rulePreview.primary.length > 0 && (
                            <span className="text-muted">{` (${rulePreview.primary.slice(0, 8).join(', ')}${rulePreview.primary.length > 8 ? ', …' : ''})`}</span>
                          )}
                        </div>
                        {rulePreview.alternative.length > 0 && (
                          <div className="text-muted" title={rulePreview.alternative.join(', ')}>
                            {t('Further matching offer for {{count}} booths (another offer is used for booking)', { count: rulePreview.alternative.length })}
                          </div>
                        )}
                        {rulePreview.toOthers.length > 0 && (
                          <div className="mt-1">
                            {t('{{count}} matching booths go to other offers:', { count: rulePreview.toOthers.length })}
                            {' '}
                            <span className="text-muted">
                              {rulePreview.toOthers.slice(0, 12).map((entry) => {
                                const other = previewData?.eventProducts.find((item) => item.id === entry.eventProductId)?.product;
                                const otherLabel = other ? (cleanCode(other.boothVariant) || cleanCode(other.boothNumber) || other.sku) : entry.eventProductId;
                                const reason = {
                                  number: t('single booth'), variant: t('plan code'), code: t('plan code'), rule: t('rule'),
                                }[entry.via] || entry.via;
                                return `${entry.boothNumber} → ${otherLabel} (${reason})`;
                              }).join(', ')}
                              {rulePreview.toOthers.length > 12 ? ', …' : ''}
                            </span>
                          </div>
                        )}
                        {rulePreview.ambiguous.length > 0 && (
                          <div className="text-warning mt-1">
                            {t('Another offer of this event has a rule with the same priority for the same features: {{list}}. Give one of them a higher priority.', {
                              list: rulePreview.ambiguous.map((w) => w.ref).join(', '),
                            })}
                          </div>
                        )}
                        {!previewLinkedEp && (
                          <div className="text-muted mt-1">
                            {t('This offer is not linked to the event yet: the preview treats it as the newest offer and uses the product price.')}
                          </div>
                        )}
                        <div className="text-muted mt-1">{t('The preview uses the values of this form, also unsaved ones.')}</div>
                      </>
                    )}
                  </div>
                </MDBCol>

                {/* ----- price rule */}
                <MDBCol lg="6">
                  <h6>{t('Price rule')}</h6>
                  <MDBRadio
                    name="boothPriceMode"
                    id="boothPriceModeFixed"
                    label={t('Fixed price per booth (the base price)')}
                    checked={priceRuleForm.mode !== 'perSqm'}
                    onChange={() => updatePriceRule({ mode: 'fixed' })}
                  />
                  <MDBRadio
                    name="boothPriceMode"
                    id="boothPriceModePerSqm"
                    label={t('Price per m²: base price × m² of the booth')}
                    checked={priceRuleForm.mode === 'perSqm'}
                    onChange={() => updatePriceRule({ mode: 'perSqm' })}
                  />
                  <div className="form-text mb-2">
                    {t('Base price: the price above, or the price of the event (Events card) when it has one. The m² come from the hall plan (data-size), else from the area of this offer.')}
                  </div>
                  {priceRuleForm.invalid && (
                    <div className="alert alert-warning small">
                      {t('The stored price rule is invalid and is ignored: {{errors}}. Saving replaces it with the rule below.', {
                        errors: priceRuleForm.invalid.join('; '),
                      })}
                    </div>
                  )}

                  <div className="small fw-bold mb-1">{t('Surcharges')}</div>
                  {priceRuleForm.surcharges.length === 0 && (
                    <p className="small text-muted mb-2">{t('No surcharges: every booth costs the base price.')}</p>
                  )}
                  {priceRuleForm.surcharges.length > 0 && (
                    <div className="table-responsive mb-2">
                      <table className="table table-sm align-middle mb-0">
                        <thead>
                          <tr>
                            <th className="small">{t('Feature')}</th>
                            <th className="small">{t('Key')}</th>
                            <th className="small">{t('Type')}</th>
                            <th className="small">{t('Value')}</th>
                            <th className="small">{t('Only on the first m²')}</th>
                            <th aria-label={t('Actions')} />
                          </tr>
                        </thead>
                        <tbody>
                          {priceRuleForm.surcharges.map((row, index) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <tr key={index}>
                              <td>
                                <select
                                  className="form-select form-select-sm"
                                  value={row.on}
                                  aria-label={t('Feature')}
                                  onChange={(e) => updateSurcharge(index, { on: e.target.value })}
                                >
                                  <option value="positionType">{t('Position type')}</option>
                                  <option value="zone">{t('Zone')}</option>
                                </select>
                              </td>
                              <td style={{ minWidth: '110px' }}>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  list={row.on === 'zone' ? 'booth-price-zone-keys' : 'booth-price-position-keys'}
                                  value={row.key}
                                  aria-label={t('Key')}
                                  onChange={(e) => updateSurcharge(index, { key: e.target.value })}
                                />
                              </td>
                              <td>
                                <select
                                  className="form-select form-select-sm"
                                  value={row.type}
                                  aria-label={t('Type')}
                                  onChange={(e) => updateSurcharge(index, { type: e.target.value, ...(e.target.value === 'percent' ? {} : { capSqm: '' }) })}
                                >
                                  {Object.entries(SURCHARGE_TYPE_LABELS).map(([value, label]) => (
                                    <option key={value} value={value} disabled={value === 'rate' && priceRuleForm.mode !== 'perSqm'}>{t(label)}</option>
                                  ))}
                                </select>
                              </td>
                              <td style={{ minWidth: '90px' }}>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="form-control form-control-sm"
                                  value={row.value}
                                  aria-label={t('Value')}
                                  onChange={(e) => updateSurcharge(index, { value: e.target.value })}
                                />
                              </td>
                              <td style={{ minWidth: '90px' }}>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="form-control form-control-sm"
                                  value={row.capSqm}
                                  placeholder={row.type === 'percent' && priceRuleForm.mode === 'perSqm' ? t('all') : ''}
                                  disabled={row.type !== 'percent' || priceRuleForm.mode !== 'perSqm'}
                                  aria-label={t('Only on the first m²')}
                                  onChange={(e) => updateSurcharge(index, { capSqm: e.target.value })}
                                />
                              </td>
                              <td className="text-end">
                                <MDBBtn
                                  type="button"
                                  color="danger"
                                  outline
                                  size="sm"
                                  title={t('Remove')}
                                  aria-label={t('Remove')}
                                  onClick={() => updatePriceRule({ surcharges: priceRuleForm.surcharges.filter((_, i) => i !== index) })}
                                >
                                  <MDBIcon fas icon="trash-alt" />
                                </MDBBtn>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <datalist id="booth-price-position-keys">
                        {cleanKeyList([...STANDARD_POSITION_KEYS, ...featureKeys.positionTypes]).map((key) => <option key={key} value={key} />)}
                      </datalist>
                      <datalist id="booth-price-zone-keys">
                        {featureKeys.zones.map((key) => <option key={key} value={key} />)}
                      </datalist>
                    </div>
                  )}
                  <MDBBtn
                    type="button"
                    color="primary"
                    outline
                    size="sm"
                    className="mb-2"
                    onClick={() => updatePriceRule({ surcharges: [...priceRuleForm.surcharges, newSurchargeRow()] })}
                  >
                    <MDBIcon fas icon="plus" className="me-2" />
                    {t('Add surcharge')}
                  </MDBBtn>
                  <div className="form-text mb-3">
                    {t('Percent: on the base price (per m²: optionally only on the first m²). Amount: added per booth. Own rate per m²: replaces the base rate for booths with this key (per m² only).')}
                  </div>
                  <MDBRow className="g-2 mb-2">
                    <MDBCol sm="4">
                      <MDBInput
                        label={t('Minimum price')}
                        type="number"
                        min="0"
                        step="0.01"
                        size="sm"
                        value={priceRuleForm.minPrice}
                        onChange={(e) => updatePriceRule({ minPrice: e.target.value })}
                      />
                    </MDBCol>
                    <MDBCol sm="4">
                      <MDBInput
                        label={t('Minimum area (m²)')}
                        type="number"
                        min="0"
                        step="0.01"
                        size="sm"
                        value={priceRuleForm.minSqm}
                        onChange={(e) => updatePriceRule({ minSqm: e.target.value })}
                      />
                    </MDBCol>
                    <MDBCol sm="4">
                      <select
                        className="form-select form-select-sm"
                        value={priceRuleForm.roundSqm}
                        aria-label={t('Rounding of the m²')}
                        title={t('Rounding of the m²')}
                        onChange={(e) => updatePriceRule({ roundSqm: e.target.value })}
                      >
                        {Object.entries(ROUND_SQM_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>{t(label)}</option>
                        ))}
                      </select>
                    </MDBCol>
                    <MDBCol size="12">
                      <div className="form-text mt-0">
                        {t('The minimum area is stored with the price rule but does not change the price; to exclude smaller booths use "Size from" of the match rule.')}
                      </div>
                    </MDBCol>
                  </MDBRow>

                  {/* example prices of the booths this offer is the primary offer for */}
                  <div className="border rounded p-2 small" data-testid="offer-price-examples">
                    <div className="fw-bold mb-1">{t('Example prices from the active plan')}</div>
                    {!previewEventId && <span className="text-muted">{t('Choose an event above.')}</span>}
                    {previewEventId && preview.loading && !rulePreview && <MDBSpinner size="sm" />}
                    {rulePreview && rulePreview.prices.count === 0 && rulePreview.prices.missing.length === 0 && (
                      <span className="text-muted">{t('This offer is not the primary offer of any booth of the plan.')}</span>
                    )}
                    {rulePreview && rulePreview.prices.count > 0 && (
                      <div>
                        {rulePreview.prices.min === rulePreview.prices.max
                          ? t('{{price}} for {{count}} booths', { price: transformMoney(rulePreview.prices.min), count: rulePreview.prices.count })
                          : t('{{min}} to {{max}} for {{count}} booths', {
                            min: transformMoney(rulePreview.prices.min),
                            max: transformMoney(rulePreview.prices.max),
                            count: rulePreview.prices.count,
                          })}
                      </div>
                    )}
                    {rulePreview && rulePreview.prices.missing.length > 0 && (
                      <div className="text-danger">
                        {t('No price for {{count}} booths (no m² for the price per m², or no base price): {{list}}', {
                          count: rulePreview.prices.missing.length,
                          list: rulePreview.prices.missing.slice(0, 10).join(', '),
                        })}
                      </div>
                    )}
                    {rulePreview && rulePreview.prices.examples.length > 0 && (
                      <table className="table table-sm mb-0 mt-1">
                        <thead>
                          <tr>
                            <th className="small">{t('Booth')}</th>
                            <th className="small">{t('m²')}</th>
                            <th className="small">{t('Features')}</th>
                            <th className="small text-end">{t('Price')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rulePreview.prices.examples.map(({ boothNumber, area, details }) => (
                            <tr key={boothNumber}>
                              <td className="fw-bold">{boothNumber}</td>
                              <td>{details.sqm !== null && details.sqm !== undefined ? details.sqm : '-'}</td>
                              <td className="text-muted">
                                {[positionLabel(area?.positionType, t), area?.zone ? t('Zone {{zone}}', { zone: area.zone }) : ''].filter(Boolean).join(' · ') || '-'}
                              </td>
                              <td className="text-end text-nowrap">
                                <div className="fw-bold">{transformMoney(details.total)}</div>
                                {details.surcharges.map((s, i) => (
                                  // eslint-disable-next-line react/no-array-index-key
                                  <div key={i} className="text-muted">
                                    {`+ ${transformMoney(s.amount)} (${s.key}${s.type === 'percent' ? ` ${s.value} %` : ''})`}
                                  </div>
                                ))}
                                {details.tier && <div className="text-muted">{t('time tier')}</div>}
                                {details.minPriceApplied && <div className="text-muted">{t('minimum price')}</div>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {previewLinkedEp && hasRuleValue(previewLinkedEp.boothPriceRule) && (
                      <div className="alert alert-warning small mt-2 mb-0">
                        {t('This event has its own price rule for this offer: the price rule above is not used for this event.')}
                        <MDBBtn
                          type="button"
                          color="link"
                          size="sm"
                          className="p-0 ms-1"
                          disabled={eventBusy === previewLinkedEp.id}
                          onClick={() => updateEventProduct(previewLinkedEp.id, { boothPriceRule: null })}
                        >
                          {t('Use the price rule of the offer')}
                        </MDBBtn>
                      </div>
                    )}
                  </div>
                </MDBCol>
              </MDBRow>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>

        {/* ------------------------------------------------------------ Events */}
        {isEdit && (
          <MDBCol lg="7">
            <MDBCard className="h-100">
              <MDBCardBody>
                <MDBCardTitle>{t('Events')}</MDBCardTitle>
                <p className="text-muted small">
                  {t('The booth can be booked at these events. Price and stock per event; stock 0 means sold.')}
                </p>
                {(form.boothVariant.trim() || matchRuleForm.enabled) && (
                  <p className="text-muted small">
                    {t('Offer with a plan code or match rule: the stock is not used. Which booths are free comes from the hall plan of the event (areas with this plan code or matching features), its bookings and reservations.')}
                  </p>
                )}
                {(priceRuleForm.mode === 'perSqm' || priceOut.json) && (
                  <p className="text-muted small">
                    {priceRuleForm.mode === 'perSqm'
                      ? t('The event price is the base rate per m²; the booth price adds the surcharges of the price rule.')
                      : t('The event price is the base price; the booth price adds the surcharges of the price rule.')}
                  </p>
                )}
                <ErrorList errors={eventErrors} />
                {eventProducts.length === 0 ? (
                  <p className="text-muted">{t('This booth is not assigned to any event yet.')}</p>
                ) : (
                  <MDBListGroup className="mb-3">
                    {eventProducts.map((eventProduct) => (
                      <MDBListGroupItem key={eventProduct.id}>
                        <MDBRow className="g-2 align-items-center">
                          <MDBCol md="4" className="fw-bold">
                            {eventProduct.event ? getLocalizedText(eventProduct.event.name) : t('Unknown event')}
                          </MDBCol>
                          <MDBCol size="6" md="2">
                            <MDBInput
                              label={priceRuleForm.mode === 'perSqm' ? t('Price per m²') : t('Price')}
                              type="number"
                              min="0"
                              step="0.01"
                              size="sm"
                              value={numberToInput(eventProduct.price)}
                              disabled={eventBusy === eventProduct.id}
                              onChange={(e) => editEventValue(eventProduct.id, 'price', e.target.value)}
                              onBlur={() => commitEventValue(eventProduct, 'price')}
                            />
                          </MDBCol>
                          <MDBCol size="6" md="2">
                            <MDBInput
                              label={t('Stock')}
                              type="number"
                              min="0"
                              step="1"
                              size="sm"
                              value={numberToInput(eventProduct.stock)}
                              disabled={eventBusy === eventProduct.id}
                              onChange={(e) => editEventValue(eventProduct.id, 'stock', e.target.value)}
                              onBlur={() => commitEventValue(eventProduct, 'stock')}
                            />
                          </MDBCol>
                          <MDBCol size="8" md="3">
                            <MDBSwitch
                              id={`boothEventAvailable-${eventProduct.id}`}
                              label={t('Available')}
                              checked={eventProduct.isAvailable !== false}
                              disabled={eventBusy === eventProduct.id}
                              onChange={(e) => updateEventProduct(eventProduct.id, { isAvailable: e.target.checked })}
                            />
                          </MDBCol>
                          <MDBCol size="4" md="1" className="text-end">
                            <MDBBtn
                              color="danger"
                              outline
                              size="sm"
                              title={t('Remove')}
                              aria-label={t('Remove')}
                              disabled={eventBusy === eventProduct.id}
                              onClick={() => setEventProductToRemove(eventProduct)}
                            >
                              <MDBIcon fas icon="trash-alt" />
                            </MDBBtn>
                          </MDBCol>
                        </MDBRow>
                      </MDBListGroupItem>
                    ))}
                  </MDBListGroup>
                )}
                <MDBRow className="g-2 align-items-center">
                  <MDBCol md="8">
                    <MDBSelect
                      label={t('Add to event')}
                      search
                      value={selectedEventId}
                      onChange={(entry) => setSelectedEventId(entry?.value || null)}
                      data={[
                        { text: t('Select event'), value: null },
                        ...availableEvents.map((event) => ({ text: getLocalizedText(event.name), value: event.id })),
                      ]}
                    />
                  </MDBCol>
                  <MDBCol md="4">
                    <MDBBtn color="primary" onClick={addToEvent} disabled={!selectedEventId || eventBusy === 'add'}>
                      {eventBusy === 'add' && <MDBSpinner size="sm" className="me-2" />}
                      {t('Add')}
                    </MDBBtn>
                  </MDBCol>
                </MDBRow>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        )}

        {/* ------------------------------------------------------------ Components */}
        {isEdit && (
          <MDBCol lg="5">
            <MDBCard className="h-100">
              <MDBCardBody>
                <MDBCardTitle>{t('Components')}</MDBCardTitle>
                {components.length === 0 ? (
                  <p className="text-muted">{t('This booth has no components.')}</p>
                ) : (
                  <ul className="mb-3">
                    {components.map((component) => (
                      <li key={component.id}>
                        {component.qty} × {component.componentProduct ? getLocalizedText(component.componentProduct.name) : '-'}
                      </li>
                    ))}
                  </ul>
                )}
                <Link to={`/admin/product/${productId}`}>{t('Manage components in product editor')}</Link>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        )}
      </MDBRow>

      <div className="d-flex justify-content-end mt-3">
        <MDBBtn color="primary" onClick={handleSave} disabled={saving || busyUploading}>
          {saving && <MDBSpinner size="sm" className="me-2" />}
          {t('Save')}
        </MDBBtn>
      </div>

      <ConfirmModal
        open={Boolean(duplicates)}
        title={t('Booth number already exists')}
        message={
          <div>
            <p>
              {t('Another booth product already uses the booth number {{boothNumber}}:', {
                boothNumber: form.boothNumber.trim(),
              })}
            </p>
            <ul>
              {(duplicates || []).map((product) => (
                <li key={product.id}>
                  <Link to={`/admin/booth/${product.id}`} target="_blank" rel="noopener noreferrer">
                    {getLocalizedText(product.name) || product.id}
                  </Link>{' '}
                  ({product.sku})
                </li>
              ))}
            </ul>
            <p className="mb-0 small text-muted">
              {t('Booths are matched to the hall plan by booth number. Only one of them should be assigned to the same event.')}
            </p>
          </div>
        }
        confirmLabel={t('Save anyway')}
        onConfirm={persist}
        onClose={() => setDuplicates(null)}
        busy={saving}
      />

      <ConfirmModal
        open={Boolean(eventProductToRemove)}
        title={t('Remove booth from event')}
        message={t('Remove the booth from {{eventName}}? Existing bookings are not changed.', {
          eventName: eventProductToRemove?.event ? getLocalizedText(eventProductToRemove.event.name) : '',
        })}
        confirmLabel={t('Remove')}
        onConfirm={confirmRemoveEventProduct}
        onClose={() => setEventProductToRemove(null)}
        busy={Boolean(eventProductToRemove) && eventBusy === eventProductToRemove?.id}
      />
    </>
  );
}
